import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useIdleTimer } from 'react-idle-timer'

import { CacheAdapter } from '@/shared/infra'

// import { env } from '@/env'
import { Button } from '../FormComponents'
import { Dialog, DialogContent, DialogHeader } from '../ui/dialog'

interface IdleControllerProps {
  timeout: number
  children: any
}

export function IdleController(props: IdleControllerProps): JSX.Element {
  const { t } = useTranslation()
  const { timeout } = props // 8 horas
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    const loginRequired = localStorage.getItem('loginRequired')
    if (loginRequired) {
      setShowModal(JSON.parse(loginRequired))
    }
  }, [])

  const onIdle = (): void => {
    if (getRemainingTime() === 0 || showModal) {
      setShowModal(true)
      localStorage.setItem('loginRequired', JSON.stringify(false))
      try {
        import('@/shared/utils/sentry-custom-error').then(
          ({ captureBusinessEvent }) => {
            const userData = CacheAdapter.get('userData')
            const user = userData?.attributes
              ? {
                  id: userData.attributes.sub,
                  username: userData.attributes.name,
                  email: userData.attributes.email,
                }
              : undefined

            captureBusinessEvent(
              'user_session_timeout',
              {
                timeoutDuration: timeout / 1000 / 60,
                remainingTime: getRemainingTime(),
                isAlreadyShowing: showModal,
                timestamp: new Date().toISOString(),
              },
              user,
              window.location.pathname,
            )
          },
        )
      } catch (sentryError) {
        console.warn('Sentry timeout event failed:', sentryError)
      }
    }
  }

  const { getRemainingTime } = useIdleTimer({
    timeout,
    onAction: onIdle,
    onIdle,
  })

  useEffect(() => {
    const handleLoginRequiredChange = (event: any): void => {
      setShowModal(event.detail)
    }

    window.addEventListener('login-required-changed', handleLoginRequiredChange)

    return () => {
      window.removeEventListener(
        'login-required-changed',
        handleLoginRequiredChange,
      )
    }
  }, [])

  const handleModal = useMemo(() => {
    return () =>
      (
        <Dialog
          open={showModal}
          onOpenChange={res => {
            setShowModal(res)
          }}
        >
          <DialogContent
            closeButton={false}
            onPointerDownOutside={e => {
              e.preventDefault()
            }}
            className="max-w-sm max-h-96 pb-6"
          >
            <DialogHeader>
              <p className="text-lg">{t('idleControllerTitle')}</p>
              <p>{t('idleControllerMessage')}</p>
            </DialogHeader>

            <Button
              onClick={() => {
                localStorage.setItem('loginRequired', JSON.stringify(false))
                window.dispatchEvent(
                  new CustomEvent('login-required-changed', { detail: false }),
                )
                setShowModal(false)
              }}
              className="mx-6"
            >
              {t('goBackToHub')}
            </Button>
          </DialogContent>
        </Dialog>
      ) as JSX.Element
  }, [showModal, t])

  return (
    <>
      {handleModal()}
      {props.children}
    </>
  )
}
